import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { useState } from 'react';
import { useT } from '../../i18n';
import { cn } from '../../lib/cn';

export interface ImageZoomProps {
  src: string;
  alt?: string;
  /** Thumbnail height; the full image is shown in the overlay. */
  size?: 'sm' | 'md';
  className?: string;
}

const SIZES = {
  sm: 'max-h-16',
  md: 'max-h-40',
} as const;

/**
 * Screenshot thumbnail that opens full size on click.
 *
 * Only the box is constrained — the stored image keeps its original pixels, so
 * the overlay shows the screenshot as it was taken while the page layout stays
 * intact whatever the image's dimensions are.
 */
export const ImageZoom = ({ src, alt, size = 'md', className }: ImageZoomProps) => {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const label = alt && alt.length > 0 ? alt : t('richText.screenshot');

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>
        <button
          type="button"
          className={cn(
            'group block w-fit max-w-full overflow-hidden rounded-md border border-border bg-sunken',
            'transition-colors hover:border-border-strong',
            className,
          )}
          aria-label={t('richText.openScreenshot', { name: label })}
        >
          <img
            src={src}
            alt={label}
            loading="lazy"
            className={cn('block w-auto max-w-full object-contain', SIZES[size])}
          />
        </button>
      </Dialog.Trigger>

      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/70" />
        <Dialog.Content
          className="fixed inset-4 z-50 flex items-center justify-center outline-none"
          aria-describedby={undefined}
        >
          <Dialog.Title className="sr-only">{label}</Dialog.Title>
          <img
            src={src}
            alt={label}
            className="max-h-full max-w-full rounded-lg border border-border bg-surface object-contain shadow-lg"
          />
          <Dialog.Close
            className="absolute right-0 top-0 inline-flex size-9 items-center justify-center rounded-md bg-surface text-ink-muted shadow-lg hover:text-ink"
            aria-label={t('common.close')}
          >
            <X className="size-4" />
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
